import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Chip,
  Divider,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { useLazyInView } from '../../hooks/useLazyInView';

export const PERSON_SUMMARY_PRIMARY = '#40518A';

const STATUS_TONES = {
  active: { bgcolor: '#dcfce7', color: '#166534' },
  partial: { bgcolor: '#fef3c7', color: '#92400e' },
  deactive: { bgcolor: '#fee2e2', color: '#991b1b' },
};

const stopCardActivation = (event) => {
  event.stopPropagation();
};

/**
 * Presentational summary card for a person directory row.
 * Callers supply image, status, detail lines, and action nodes so the same
 * card can be reused for employees, vendors, visitors, and intrusion profiles.
 */
const PersonSummaryCard = ({
  name = 'Unknown person',
  code = '',
  imageSrc = '',
  category = '',
  statusLabel = '',
  statusTone = 'active',
  details = [],
  meta = null,
  muted = false,
  accentColor = PERSON_SUMMARY_PRIMARY,
  onClick = null,
  onImageClick = null,
  onRequestImage = null,
  actions = null,
  footer = null,
}) => {
  const lazyRef = useLazyInView(() => {
    if (typeof onRequestImage === 'function') onRequestImage();
  }, { once: true });

  const initial = (name || '?').charAt(0).toUpperCase();
  const tone = STATUS_TONES[statusTone] || STATUS_TONES.active;
  const interactive = typeof onClick === 'function';

  const handleImageClick = (event) => {
    if (!imageSrc || typeof onImageClick !== 'function') return;
    event.preventDefault();
    event.stopPropagation();
    onImageClick(event);
  };

  return (
    <Card
      ref={lazyRef}
      variant="outlined"
      onClick={interactive ? onClick : undefined}
      onKeyDown={interactive ? (event) => {
        if (event.target !== event.currentTarget) return;
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onClick(event);
        }
      } : undefined}
      role={interactive ? 'button' : undefined}
      tabIndex={interactive ? 0 : undefined}
      aria-label={interactive ? `Open details for ${name}` : undefined}
      sx={{
        borderRadius: 3,
        width: '100%',
        height: '100%',
        bgcolor: '#ffffff',
        borderTop: `3px solid ${muted ? '#94a3b8' : accentColor}`,
        display: 'flex',
        flexDirection: 'column',
        boxShadow: '0 10px 30px rgba(15, 23, 42, 0.21)',
        cursor: interactive ? 'pointer' : 'default',
        opacity: muted ? 0.85 : 1,
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        '&:hover': interactive ? {
          boxShadow: '0 20px 40px rgba(15,23,42,0.15)',
          transform: 'translateY(-4px)',
          borderColor: muted ? '#94a3b8' : accentColor,
        } : undefined,
        '&:focus-visible': interactive ? {
          outline: `3px solid ${accentColor}`,
          outlineOffset: 2,
        } : undefined,
        '&:active': interactive ? { transform: 'translateY(-2px)' } : undefined,
      }}
    >
      <CardContent
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: { xs: 1.25, sm: 1.5 },
          flex: 1,
          p: { xs: 1.5, sm: 2 },
          '&:last-child': { pb: { xs: 1.5, sm: 2 } },
        }}
      >
        <Box
          onClick={handleImageClick}
          sx={{
            position: 'relative',
            width: '100%',
            aspectRatio: '3/4',
            borderRadius: 2,
            overflow: 'hidden',
            bgcolor: '#EEF2FF',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            cursor: imageSrc && onImageClick ? 'zoom-in' : 'inherit',
          }}
        >
          {imageSrc ? (
            <Box
              component="img"
              src={imageSrc}
              alt={name}
              data-no-image-preview="true"
              sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
            />
          ) : (
            <Typography sx={{ fontSize: '2.75rem', fontWeight: 600, color: '#374151', lineHeight: 1 }}>
              {initial}
            </Typography>
          )}
          <Box
            sx={{
              position: 'absolute',
              left: 0,
              right: 0,
              bottom: 0,
              px: 1.5,
              py: 1.25,
              background: 'linear-gradient(to top, rgba(15, 23, 42, 0.78) 0%, rgba(15, 23, 42, 0.35) 58%, transparent 100%)',
              pointerEvents: 'none',
            }}
          >
            <Typography
              noWrap
              sx={{ color: '#fff', fontWeight: 700, fontSize: { xs: '0.95rem', sm: '1.05rem' }, lineHeight: 1.25 }}
            >
              {name}
            </Typography>
            {code ? (
              <Typography
                noWrap
                sx={{ color: 'rgba(255,255,255,0.88)', fontSize: { xs: '0.72rem', sm: '0.78rem' }, display: 'block' }}
              >
                {code}
              </Typography>
            ) : null}
          </Box>
        </Box>
        {details.length > 0 && (
          <>
            <Divider />
            <Stack spacing={0.75}>
              {details.map((detail) => (
                <Stack
                  key={detail.label}
                  direction="row"
                  spacing={1}
                  alignItems="baseline"
                  justifyContent="space-between"
                  sx={{ minWidth: 0 }}
                >
                  <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
                    {detail.label}
                  </Typography>
                  <Tooltip title={detail.value || ''} arrow disableHoverListener={!detail.value || detail.value === '—'}>
                    <Typography
                      variant="body2"
                      noWrap
                      sx={{
                        minWidth: 0,
                        textAlign: 'right',
                        fontSize: { xs: '0.8rem', sm: '0.85rem' },
                        color: !detail.value || detail.value === '—' ? 'text.disabled' : 'text.primary',
                      }}
                    >
                      {detail.value || '—'}
                    </Typography>
                  </Tooltip>
                </Stack>
              ))}
            </Stack>
          </>
        )}

        {meta?.value ? (
          <Typography variant="caption" color="text.secondary" noWrap>
            {meta.label ? `${meta.label}: ` : ''}{meta.value}
          </Typography>
        ) : null}

        {actions ? (
          <Box
            sx={{ mt: 'auto', pt: 0.5 }}
            onClick={stopCardActivation}
            onKeyDown={stopCardActivation}
          >
            {actions}
          </Box>
        ) : null}

        {footer ? (
          <Box onClick={stopCardActivation} onKeyDown={stopCardActivation}>
            {footer}
          </Box>
        ) : null}
      </CardContent>
    </Card>
  );
};

export default PersonSummaryCard;
